'use client'

import React, { useMemo } from 'react'
import moment from 'moment'
import { DcChart, formatCurrency } from 'pixel-base'

import { formatDateForApi } from 'pixel-base'
import { get } from 'utils/api-routes.util'

interface CityData {
  city: string
  state: string
  country: string
  total_orders: number
  successful_orders: number
  returned_orders: number
  net_revenue: number
  returned_revenue: number
  avg_order_value: number
  unique_customers: number
  return_rate_percentage: number
  success_rate_percentage: number
  revenue_return_rate_percentage: number
  orders_per_customer: number
}

interface TopCitiesProps {
  filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }
  height?: number
  setComponent: (component: string) => void
  setShowDetail: (show: boolean) => void
  width?: string
}

const TopCitiesChart: React.FC<TopCitiesProps> = ({
  filters,
  height = 350,
  setComponent: _setComponent,
  setShowDetail: _setShowDetail,
  width: _width,
}) => {
  const [data, setData] = React.useState<CityData[]>([])
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    setLoading(true)
    get('/dashboard/cities', {
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
      data_type: filters.data_type,
      limit: -1,
    }).subscribe({
      next: (res: { response?: { data: CityData[] }; data?: CityData[] }) => {
        // Handle AjaxResponse format
        let responseData = null
        if (res && res.response && res.response.data) {
          responseData = res.response.data
        } else if (res && res.data) {
          responseData = res.data
        }

        if (responseData && Array.isArray(responseData)) {
          setData(responseData)
        } else {
          setData([])
        }
        setLoading(false)
      },
      error: (error) => {
        console.error('Error fetching cities data:', error)
        setData([])
        setLoading(false)
      },
    })
  }, [filters.start_date, filters.end_date, filters.data_type])

  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        yAxis: [],
        series: [],
      }
    }

    // Sort by total orders and take top 10
    const sortedData = [...data]
      .sort((a, b) => b.total_orders - a.total_orders)
      .slice(0, 10)

    return {
      yAxis: sortedData.map((city) => city.city).reverse(),
      series: [
        {
          name: 'Successful Orders',
          type: 'bar',
          stack: 'orders',
          data: sortedData.map((city) => city.successful_orders).reverse(),
          itemStyle: {
            color: '#10b981', // Green color for successful orders
          },

          barWidth: '80%',
        },
        {
          name: 'Returned Orders',
          type: 'bar',
          stack: 'orders',
          data: sortedData.map((city) => city.returned_orders).reverse(),
          itemStyle: {
            color: '#ef4444', // Red color for returned orders
          },
        },
      ],
    }
  }, [data])

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        axis: 'y',
        snap: true,
        lineStyle: {
          color: '#5f38f9',
          width: 1,
        },
      },
      position: 'top',
      backgroundColor: '#ffffff',
      borderColor: '#e0e0e0',
      borderWidth: 1,
      extraCssText: 'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);',
      textStyle: {
        color: '#333333',
      },
      formatter: (params: Array<{ axisValue: string; dataIndex: number }>) => {
        if (!params || !Array.isArray(params) || params.length === 0) return ''

        const cityName = params[0].axisValue
        const cityData = data.find((city) => city.city === cityName)

        if (!cityData) return ''

        return `
          <div style="display: flex; gap: 16px; align-items: start; max-width: 550px; padding: 12px; background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.1);">
            <div style="display: flex; flex-direction: column; gap: 8px; flex-shrink: 0;">
              <div style="width: 60px; height: 60px; background: linear-gradient(135deg, #5f38f9 0%, #7c3aed 100%); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 600; font-size: 14px;">
                ${cityData.city.substring(0, 2).toUpperCase()}
              </div>
            </div>
            <div style="flex: 1; display: flex; flex-direction: column; gap: 12px;">
              <div>
                <div style="font-size: 16px; font-weight: 600; color: #1f2937; margin-bottom: 4px;">${
                  cityData.city
                }</div>
                <div style="font-size: 12px; color: #6b7280;">${
                  cityData.state
                }, ${cityData.country}</div>
              </div>
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Total Orders:</span>
                  <span style="font-weight: 600; color: #1f2937;">${cityData.total_orders.toLocaleString()}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Successful:</span>
                  <span style="font-weight: 600; color: #10b981;">${cityData.successful_orders.toLocaleString()}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Returned:</span>
                  <span style="font-weight: 600; color: #ef4444;">${cityData.returned_orders.toLocaleString()}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Success Rate:</span>
                  <span style="font-weight: 600; color: #10b981;">${cityData.success_rate_percentage.toFixed(
                    1,
                  )}%</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Return Rate:</span>
                  <span style="font-weight: 600; color: #ef4444;">${cityData.return_rate_percentage.toFixed(
                    1,
                  )}%</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Net Revenue:</span>
                  <span style="font-weight: 600; color: #10b981;">${formatCurrency(
                    cityData.net_revenue,
                  )}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Avg Order Value:</span>
                  <span style="font-weight: 600; color: #1f2937;">${formatCurrency(
                    cityData.avg_order_value,
                  )}</span>
                </div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: #6b7280;">Unique Customers:</span>
                  <span style="font-weight: 600; color: #1f2937;">${cityData.unique_customers.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        `
      },
    },
    legend: {
      orient: 'horizontal',
      top: 25,
      left: 'center',
      padding: [0, 10, 10, 10],
      textStyle: {
        fontSize: 12,
        color: '#333',
      },
    },
    grid: {
      top: 60,
      bottom: 30,
    },
  }

  // Calculate totals for display
  const totalOrders = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce((sum, city) => sum + (city.total_orders || 0), 0)
  }, [data])

  const totalRevenue = useMemo(() => {
    if (!data || !Array.isArray(data)) return 0
    return data.reduce((sum, city) => sum + (city.net_revenue || 0), 0)
  }, [data])

  return (
    <DcChart
      title="Top Cities"
      isLoading={loading}
      isTitleClickable={true}
      onClickTitle={() => {
        _setComponent('topCitiesChart')
        _setShowDetail(true)
      }}
      type="horizontalBar"
      data={chartData}
      height={height}
      data-testid="chart"
      toggleData={[
        {
          title: 'Total Orders',
          data: totalOrders.toLocaleString(),
        },
        {
          title: 'Total Revenue',
          data: formatCurrency(totalRevenue),
        },
      ]}
      options={chartOptions}
    />
  )
}

export default TopCitiesChart
